/**
 * reportRange — IDI-777: the From/To range decides what a report shows.
 *
 * Every report with a date range filters the lists it already loads (D1) by
 * the row's own date (D2), read as the dealer's calendar day (D3,
 * America/Chicago, both ends included). A record with no date stays and sits
 * under "No Date" (D6); a From after To keeps nothing (D5's empty state).
 */
import { dealerToday } from './dealerDate';
import { isJobCompleted } from './jobCompletion';
import { quoteDateValue } from './normaliseSalespeople';

const BARE_DAY = /^\d{4}-\d{2}-\d{2}$/;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * The dealer's day (YYYY-MM-DD) a date value falls on. A bare day is already
 * a dealer's day and is returned as is; an instant is read in Chicago.
 * @returns {string|null}
 */
export function dealerDayOf(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && BARE_DAY.test(value)) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return dealerToday(d);
}

const createdOf = (item) => item?.createdAt || item?.created_at;

/**
 * D2: the date that places a record in a report.
 * - Revenue: paid, else invoiced, else created.
 * - Job Performance: completed (IDI-246 order) when completed, else created.
 * - Quote reports: the Quote Date (`date`, else created).
 * - Other job reports: created.
 */
export function reportDateOf(report, item) {
  if (!item) return undefined;
  const id = report?.id;
  if (id === 'revenue') {
    return item.paidDate || item.paid_date || item.invoicedDate || item.invoiced_date || createdOf(item);
  }
  if (id === 'job_performance') {
    if (isJobCompleted(item)) {
      const done = item.completedDate || item.completed_date || item.completedAt || item.completed_at;
      if (done) return done;
    }
    return createdOf(item);
  }
  if (report?.dataSource === 'quotes') return quoteDateValue(item);
  return createdOf(item);
}

/** The report day of a record, as the dealer's YYYY-MM-DD (or null). */
export function reportDayOf(report, item) {
  return dealerDayOf(reportDateOf(report, item));
}

/** Does a report have a From/To range? */
export function hasDateRange(report) {
  return !!report?.filters?.some((f) => f.type === 'date_range');
}

/**
 * Is a record's day in [start, end]? Undated records stay (D6) unless the
 * range itself is inverted, which keeps nothing (D5).
 */
export function inRange(day, start, end) {
  if (start && end && start > end) return false;
  if (!day) return true;
  if (start && day < start) return false;
  if (end && day > end) return false;
  return true;
}

/** "YYYY-MM" for a day, or null. */
export function monthKeyOf(day) {
  return day ? day.slice(0, 7) : null;
}

/** Every "YYYY-MM" from start's month to end's month, inclusive. */
export function monthKeysBetween(start, end) {
  if (!start || !end || start > end) return [];
  let [y, m] = start.slice(0, 7).split('-').map(Number);
  const [ey, em] = end.slice(0, 7).split('-').map(Number);
  const keys = [];
  while (y < ey || (y === ey && m <= em)) {
    keys.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return keys;
}

/** "October 2026" (long), "Feb" (short) or "Feb 2025" (short with year). */
export function monthLabel(key, { long = false, withYear = long } = {}) {
  const [y, m] = key.split('-').map(Number);
  const name = MONTH_NAMES[m - 1] || '';
  const shown = long ? name : name.slice(0, 3);
  return withYear ? `${shown} ${y}` : shown;
}
